import { DynamicStructuredTool } from '@langchain/core/tools';
import { z } from 'zod';
import { formatToolResult, type ToolResult } from '../types.js';
import { callMcpToolByName } from './drishti-mcp-client.js';

function upperSymbol(symbol: string): string {
  return symbol.trim().toUpperCase();
}

function parseEnvelope(raw: string): ToolResult {
  try {
    const parsed = JSON.parse(raw) as ToolResult;
    return {
      data: parsed.data,
      sourceUrls: parsed.sourceUrls,
    };
  } catch {
    return { data: raw };
  }
}

async function safeMcpCall(name: string, args: Record<string, unknown>): Promise<ToolResult> {
  try {
    return parseEnvelope(await callMcpToolByName(name, args));
  } catch (error) {
    return {
      data: {
        error: error instanceof Error ? error.message : String(error),
      },
      sourceUrls: [`mcp://drishti/${name}`],
    };
  }
}

function collectUrls(results: ToolResult[]): string[] {
  return [...new Set(results.flatMap((result) => result.sourceUrls ?? []))];
}

export const TOP_MOVERS_DESCRIPTION =
  'MCP-backed direct lookup for today/session top Indian stock gainers and losers. Use for "top movers", "top gainers", "top losers", or biggest movers queries.';

export function createTopMoversTool(): DynamicStructuredTool {
  return new DynamicStructuredTool({
    name: 'top_movers',
    description: TOP_MOVERS_DESCRIPTION,
    schema: z.object({
      market_cap_min: z.number().optional().describe('Minimum market cap filter in crores. Default 1000.'),
      top_n: z.number().int().min(1).max(25).optional().describe('Number of gainers and losers to return. Default 5.'),
    }),
    func: async (input) => {
      return callMcpToolByName('get_top_movers', {
        market_cap_min: input.market_cap_min ?? 1000,
        top_n: input.top_n ?? 5,
      });
    },
  });
}

export const PRICE_VOLUME_DESCRIPTION =
  'MCP-backed direct price/volume lookup for an Indian listed symbol. Use when price move, volume, 52-week, or trading context is needed for one symbol.';

export function createPriceVolumeTool(): DynamicStructuredTool {
  return new DynamicStructuredTool({
    name: 'price_volume',
    description: PRICE_VOLUME_DESCRIPTION,
    schema: z.object({
      symbol: z.string().describe('Known Indian listed symbol, e.g. RELIANCE, TCS, SHAREINDIA.'),
    }),
    func: async (input) => {
      return callMcpToolByName('get_price_and_volume', {
        symbols: [upperSymbol(input.symbol)],
      });
    },
  });
}

export const STOCK_MOVE_BRIEF_DESCRIPTION =
  'MCP-backed quick explainer evidence pack for why a known Indian listed symbol is moving today/recently. Fetches price/volume, recent news, and recent announcements in one tool call.';

export function createStockMoveBriefTool(): DynamicStructuredTool {
  return new DynamicStructuredTool({
    name: 'stock_move_brief',
    description: STOCK_MOVE_BRIEF_DESCRIPTION,
    schema: z.object({
      symbol: z.string().describe('Known Indian listed symbol, e.g. RELIANCE, TCS, SHAREINDIA.'),
      limit: z.number().int().min(1).max(10).optional().describe('Max recent news/announcement records per source. Default 5.'),
    }),
    func: async (input) => {
      const symbol = upperSymbol(input.symbol);
      const limit = input.limit ?? 5;
      const [priceVolume, news, announcements] = await Promise.all([
        safeMcpCall('get_price_and_volume', { symbols: [symbol] }),
        safeMcpCall('get_news', { symbols: [symbol], limit }),
        safeMcpCall('get_announcements', { symbols: [symbol], limit }),
      ]);

      return formatToolResult(
        {
          symbol,
          price_volume: priceVolume.data,
          news: news.data,
          announcements: announcements.data,
          guidance:
            'Use this evidence to explain confirmed catalysts first. If news/announcements are empty or stale, say no fresh source-backed catalyst was found and label technical/momentum explanations as inference.',
        },
        collectUrls([priceVolume, news, announcements]),
      );
    },
  });
}
